<template>
  <div class="introduction-page" ref="pageRef">
    <!-- 顶部背景横幅 -->
    <div class="page-banner"></div>

    <!-- 主体内容区 -->
    <div class="page-content" ref="contentRef">
      <!-- 面包屑 -->
      <div class="breadcrumb">
        <span class="breadcrumb-icon">🏠</span>
        <span class="breadcrumb-text">当前位置：<span class="breadcrumb-link" @click="goHome">首页</span> / {{ parentNavName }}</span>
      </div>

      <div class="content-wrapper">
        <!-- 左侧菜单 -->
        <div class="sidebar">
          <div class="sidebar-title">{{ parentNavName }}</div>
          <ul class="sidebar-menu">
            <li 
              v-for="item in menuItems" 
              :key="item.id"
              class="menu-item" 
              :class="{ active: activeTab === item.id }"
              @click="handleMenuClick(item)"
            >
              <span>{{ item.name }}</span>
              <span class="arrow">›</span>
            </li>
          </ul>
        </div>

        <!-- 右侧内容 -->
        <div class="main-content">
          <Detail 
            v-if="showDetail" 
            :title="detailData.title" 
            :content="detailData.content" 
            :author="detailData.author"
            :create_time="detailData.create_time"
            :showCloseBtn="false" 
          />
          <TextListPage v-else :listData="listData" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick, watch } from 'vue'
import { useRouter } from 'vue-router'
import sys from '@/api/index'
import Detail from './detail.vue'
import TextListPage from './textListPage.vue'

const router = useRouter()

const activeTab = ref('')
const menuItems = ref([])
const parentNavName = ref('政策文件')
const showDetail = ref(true)
const detailData = ref({ title: '', content: '', author: '', create_time: '' })
const listData = ref([])
const pageRef = ref(null)
const contentRef = ref(null)

// 跳转到首页
const goHome = () => {
  router.push('/')
}

// 获取导航数据
const getNavMainList = async () => {
  try {
    const res = await sys.getList({ ident: 'document' })
    if (res && res.code === 2000 && res.data && Array.isArray(res.data)) {
      // 直接使用返回的数据生成菜单
      if (res.data.length > 0) {
        parentNavName.value = '政策文件'
        
        // 将返回的数据映射为菜单项，保留 ident 字段
        menuItems.value = res.data.map(item => ({
          name: item.name,
          id: item.id,
          ident: item.ident
        }))
        
        // 默认选中第一项
        if (menuItems.value.length > 0) {
          activeTab.value = menuItems.value[0].id
          // 默认加载第一项的数据
          handleMenuClick(menuItems.value[0])
        }
      }
    }
  } catch (error) {
    console.error('获取导航数据失败:', error)
    // 使用默认菜单
    menuItems.value = [
      { name: '政策法规', id: 'policy', ident: 'policy' },
      { name: '协会文件', id: 'association', ident: 'association' }
    ]
  }
}

// 处理菜单点击事件
const handleMenuClick = async (item) => {
  activeTab.value = item.id
  
  // 调用 getTopList 接口，传入当前项的 id
  try {
    const res = await sys.getTopList({cid:item.id})
    if (res && res.code === 2000) {
      console.log('获取详情数据成功:', res.data)
      
      // 判断返回的数据数量，数据在 res.data.data 中
      const dataList = res.data?.data || []
      
      if (dataList.length === 0) {
        // 没有数据，清空右侧内容
        showDetail.value = true
        detailData.value = {
          title: '',
          content: '',
          author: '',
          create_time: ''
        }
        listData.value = []
      } else if (dataList.length === 1) {
        // 只有一条数据，显示 Detail 组件
        showDetail.value = true
        detailData.value = {
          title: dataList[0].title || '',
          content: dataList[0].content || '',
          author: dataList[0].author || '',
          create_time: dataList[0].create_time || ''
        }
      } else if (dataList.length > 1) {
        // 多条数据，显示列表组件
        showDetail.value = false
        listData.value = dataList
      }
    } else {
      // 接口返回失败，清空右侧内容
      showDetail.value = true
      detailData.value = {
        title: '',
        content: '',
        author: '',
        create_time: ''
      }
      listData.value = []
    }
  } catch (error) {
    console.error('获取详情数据失败:', error)
    // 发生错误时，清空右侧内容
    showDetail.value = true
    detailData.value = {
      title: '',
      content: '',
      author: '',
      create_time: ''
    }
    listData.value = []
  }
  
  // 更新页面高度
  updatePageHeight()
}

// 动态计算并设置页面高度
const updatePageHeight = () => {
  nextTick(() => {
    if (contentRef.value && pageRef.value) {
      const contentHeight = contentRef.value.offsetHeight
      const contentTop = 320 // position absolute 的 top 值
      const totalHeight = contentTop + contentHeight
      pageRef.value.style.minHeight = `${totalHeight}px`
    }
  })
}

onMounted(() => {
  getNavMainList()
  updatePageHeight()
  // 监听窗口大小变化
  window.addEventListener('resize', updatePageHeight)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updatePageHeight)
})

// 监听 activeTab 变化，重新计算高度
watch(activeTab, () => {
  updatePageHeight()
})
</script>

<style scoped>
.introduction-page {
  width: 100%;
  min-height: 100vh;
  overflow-x: hidden;
}

/* 顶部横幅 */
.page-banner {
  width: 100%;
  height: 210px;
  background: url('~@/assets/images/innerbanner.jpg') center center / cover no-repeat;
  position: relative;
}

/* 主体内容 */
.page-content {
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;
  padding: 0px 20px 60px;
  position: absolute;
  top: 320px;
  left: 0;
  right: 0;
  box-sizing: border-box;
}

/* 面包屑 */
.breadcrumb {
  height: 80px;
  display: flex;
  align-items: center;
  font-size: 14px;
  background-color: #eaf7fa;
  padding: 10px 20px;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  box-sizing: border-box;
}

.breadcrumb-icon {
  margin-right: 8px;
  font-size: 16px;
  color: #4facfe;
}

.breadcrumb-text {
  color: #666;
  font-size: 1rem;
}

.breadcrumb-link {
  color: #666;
  cursor: pointer;
  transition: color 0.3s ease;
}

.breadcrumb-link:hover {
  color: #4facfe;
}

.content-wrapper {
  display: flex;
  gap: 20px;
  box-sizing: border-box;
}

/* 左侧菜单 */
.sidebar {
  width: 240px;
  flex-shrink: 0;
  background-color: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  height: fit-content;
  box-sizing: border-box;
}

.sidebar-title {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  color: #fff;
  font-size: 18px;
  font-weight: 500;
  padding: 20px 24px;
  text-align: left;
  border-radius: 8px 8px 0 0;
}

.sidebar-menu {
  list-style: none;
  padding: 0;
  margin: 0;
}

.menu-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  color: #333;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
  border-bottom: 1px solid #f0f0f0;
  background-color: #fff;
}

.menu-item:last-child {
  border-bottom: none;
}

.menu-item:hover {
  background-color: #f8f9fa;
  color: #4facfe;
}

.menu-item.active {
  background-color: #f0f8ff;
  color: #4facfe;
  font-weight: 500;
}

.menu-item .arrow {
  font-size: 18px;
  color: #999;
}

.menu-item.active .arrow,
.menu-item:hover .arrow {
  color: #4facfe;
}

/* 右侧内容 */
.main-content {
  flex: 1;
  min-width: 0;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  box-sizing: border-box;
}

.content-title {
  font-size: 26px;
  color: #333;
  margin-bottom: 30px;
  font-weight: 500;
  text-align: center;
  position: relative;
  padding-bottom: 15px;
}

.content-title::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 3px;
  background: linear-gradient(to right, #4facfe, #00f2fe);
  border-radius: 2px;
}

.content-text {
  color: #666;
  line-height: 2;
  font-size: 15px;
}

.content-text p {
  margin-bottom: 20px;
  text-align: justify;
  text-indent: 2em;
}

.content-text p:last-child {
  margin-bottom: 0;
}

/* 响应式设计 */
@media (max-width: 1240px) {
  .page-content {
    max-width: calc(100% - 40px);
  }
}

@media (max-width: 1024px) {
  .page-content {
    max-width: calc(100% - 30px);
    padding: 0px 15px 50px;
  }
  
  .sidebar {
    width: 200px;
  }
  
  .content-wrapper {
    gap: 15px;
  }
}

@media (max-width: 768px) {
  .page-banner {
    height: 120px;
  }
  
  .page-content {
    max-width: calc(100% - 20px);
    padding: 0px 10px 40px;
    top: 280px;
  }
  
  .content-wrapper {
    flex-direction: column;
    gap: 15px;
  }
  
  .sidebar {
    width: 100%;
  }
  
  .breadcrumb {
    height: auto;
    min-height: 60px;
    padding: 12px 15px;
  }
  
  .breadcrumb-text {
    font-size: 0.9rem;
  }
  
  .main-content {
    padding: 30px 20px;
  }
  
  .content-title {
    font-size: 22px;
  }
  
  .content-text {
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .page-banner {
    height: 100px;
  }
  
  .page-content {
    max-width: calc(100% - 16px);
    padding: 0px 8px 30px;
    top: 260px;
  }
  
  .breadcrumb {
    min-height: 50px;
    padding: 10px 12px;
    font-size: 12px;
  }
  
  .breadcrumb-text {
    font-size: 0.85rem;
  }
  
  .sidebar-title {
    font-size: 16px;
    padding: 16px 18px;
  }
  
  .menu-item {
    padding: 14px 18px;
    font-size: 14px;
  }
}
</style>